<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import { useEventListener } from "@vueuse/core";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import SsoService, { type TSsoOrganization } from "@/api/models/auth/sso";
import PrimaryButton from "@/components/shared/buttons/PrimaryButton.vue";
import LoginFooterLinks from "@/components/models/auth/LoginFooterLinks.vue";
import SsoOrganizationLogo from "@/components/models/auth/SsoOrganizationLogo.vue";
import { getHttpErrorStatus } from "@/router/queryErrorRedirect";
import { isSafeInternalReturnPath } from "@/router/utils";

definePage({
  meta: {
    title: "models.auth.actions.logIn",
    layout: "login",
    requiresAuth: false,
  },
});

const { t } = useI18n();
const route = useRoute("/sso/[slug]");
const router = useRouter();

const slug = route.params.slug;
const organization = ref<TSsoOrganization | null>(null);

const redirect = typeof route.query.redirect === "string" ? route.query.redirect : undefined;
const safeRedirect = redirect && isSafeInternalReturnPath(redirect) ? redirect : "/";

const isRedirecting = ref(false);

useEventListener(globalThis, "pageshow", () => {
  isRedirecting.value = false;
});

onMounted(async () => {
  try {
    const { data } = await SsoService.getOrganization(slug);
    organization.value = data;
  } catch (error) {
    if (getHttpErrorStatus(error) !== 404) {
      logError(error, { context: { feature: "sso", step: "resolve-organization" } });
    }
    await router.replace("/auth/login");
  }
});

const logIn = (loginUrl: string) => {
  const url = new URL(loginUrl);
  url.searchParams.set("next", safeRedirect);
  isRedirecting.value = true;
  globalThis.location.href = url.toString();
};
</script>

<template>
  <div
    v-if="!organization"
    class="tw:ml-auto! tw:w-full tw:lg:w-1/2 tw:h-full tw:flex tw:items-center tw:justify-center"
  >
    <v-progress-circular
      indeterminate
      color="primary"
      size="48"
      data-testid="sso-organization-loading"
    />
  </div>

  <div
    v-else
    class="tw:ml-auto! tw:w-full tw:lg:w-1/2 tw:h-full tw:flex tw:items-center tw:lg:relative"
  >
    <div class="tw:w-full tw:lg:w-2/3 tw:flex tw:flex-col">
      <div class="tw:hidden tw:lg:block tw:font-bold tw:text-2xl tw:md:text-4xl tw:!mb-1">
        {{ t("models.auth.actions.logIn") }}
      </div>

      <p class="text-medium-emphasis tw:text-sm tw:mt-6 tw:lg:mt-0">
        {{ t("models.auth.sso.subtitle", { name: organization.name }) }}
      </p>

      <div
        v-if="organization.logo"
        class="tw:mt-10! tw:flex tw:items-center tw:justify-center tw:rounded-xl tw:border tw:border-solid tw:border-black/10 tw:px-6 tw:py-8"
      >
        <SsoOrganizationLogo
          :organization="organization"
          class="tw:h-10"
        />
      </div>

      <PrimaryButton
        class="tw:mt-6! tw:w-full"
        :loading="isRedirecting"
        data-testid="sso-login-button"
        @click="logIn(organization.login_url)"
      >
        {{ t("models.auth.sso.continueWith", { name: organization.name }) }}
      </PrimaryButton>

      <div class="tw:mt-6! tw:text-center">
        <router-link
          :to="{ path: '/auth/login', query: redirect ? { redirect } : {} }"
          class="tw:text-xs text-medium-emphasis tw:no-underline tw:hover:underline"
          data-testid="sso-password-login-link"
        >
          {{ t("models.auth.sso.usePassword") }}
        </router-link>
      </div>

      <LoginFooterLinks />
    </div>
  </div>
</template>
